import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Mic, MicOff, Loader2, Sparkles, Keyboard, HelpCircle, Check, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { useMeta } from "@/lib/hooks";
import { Field, StatusPill } from "@/components/common";
import { PRESET_OFFSETS } from "@/components/ReminderSteps";
import { RECURRENCE_LABELS, countdownLabel, fmtDate } from "@/lib/brand";
import { daysLeft } from "@/lib/dates";

const SR = typeof window !== "undefined" ? window.SpeechRecognition || window.webkitSpeechRecognition : null;
const EXAMPLE = "Remind me about my consulate appointment coming Tuesday at 12 PM, one day before.";

function useSpeech(onFinal) {
  const rec = useRef(null);
  const [listening, setListening] = useState(false);
  const [interim, setInterim] = useState("");
  const [error, setError] = useState("");
  const start = () => {
    if (!SR) { setError("unsupported"); return; }
    setError(""); setInterim("");
    const r = new SR();
    r.lang = navigator.language || "en-US";
    r.interimResults = true;
    r.continuous = false;
    let finalText = "";
    r.onresult = (e) => {
      let t = "";
      for (let i = 0; i < e.results.length; i++) {
        if (e.results[i].isFinal) finalText = e.results[i][0].transcript; else t += e.results[i][0].transcript;
      }
      setInterim(finalText || t);
    };
    r.onerror = (e) => setError(e.error === "not-allowed" || e.error === "service-not-allowed" ? "denied" : e.error === "no-speech" ? "no-speech" : "failed");
    r.onend = () => { setListening(false); if (finalText.trim()) onFinal(finalText.trim()); };
    rec.current = r;
    try { r.start(); setListening(true); } catch { setError("failed"); }
  };
  const stop = () => rec.current?.stop();
  useEffect(() => () => rec.current?.abort(), []);
  return { listening, interim, error, start, stop, supported: !!SR };
}

const ERRORS = {
  unsupported: "Voice recognition isn't available in this browser. Type your reminder instead.",
  denied: "Microphone access is blocked. Allow the microphone in your browser settings, or type your reminder below.",
  "no-speech": "We didn't hear anything. Tap the microphone and try again.",
  failed: "Voice recognition stopped unexpectedly. Try again or type your reminder.",
};

function Preview({ p, setP, meta, onReparse, busy }) {
  const [answer, setAnswer] = useState("");
  const cat = meta?.categories.find((c) => c.id === p.category_id);
  const d = p.event_date ? daysLeft(p.event_date, p.recurrence, p.today, "feb28") : null;
  const set = (k) => (e) => setP({ ...p, [k]: e.target.value });
  const toggle = (o) => setP({ ...p, alert_offsets: p.alert_offsets.includes(o) ? p.alert_offsets.filter((x) => x !== o) : [...p.alert_offsets, o].sort((a, b) => b - a) });
  return (
    <div className="grid gap-4" data-testid="voice-preview">
      <p className="text-xs text-[var(--pm-muted)] bg-[#F6F4FD] rounded-xl p-3 italic" data-testid="voice-transcript">“{p.transcript}”</p>
      {p.needs_clarification && (
        <div className="rounded-xl bg-[#FEF3E2] p-4" data-testid="voice-clarification">
          <p className="text-sm font-semibold text-[#7A4705] flex gap-2"><HelpCircle className="w-4 h-4 shrink-0 mt-0.5" />{p.clarification_question}</p>
          <form className="flex gap-2 mt-3" onSubmit={(e) => { e.preventDefault(); if (answer.trim()) onReparse(`${p.transcript}. ${answer.trim()}`); }}>
            <input className="pm-input bg-white" value={answer} onChange={(e) => setAnswer(e.target.value)} placeholder="e.g. next Friday at 3 PM" data-testid="voice-clarify-input" />
            <button className="pm-btn pm-btn-primary" disabled={busy || !answer.trim()} data-testid="voice-clarify-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Answer"}</button>
          </form>
          <p className="text-xs text-[#7A4705] mt-2">Or fix the details directly below.</p>
        </div>
      )}
      <Field label="Title" htmlFor="v-title"><input id="v-title" className="pm-input" value={p.title} onChange={set("title")} data-testid="voice-title-input" /></Field>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Category"><select className="pm-input" value={p.category_id} onChange={(e) => { const c = meta.categories.find((x) => x.id === e.target.value); setP({ ...p, category_id: c.id, subcategory_id: c.subcategories[0].id }); }} data-testid="voice-category-select">
          {meta?.categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></Field>
        <Field label="Subcategory"><select className="pm-input" value={p.subcategory_id} onChange={set("subcategory_id")} data-testid="voice-subcategory-select">
          {cat?.subcategories.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select></Field>
        <Field label="Date" required error={!p.event_date ? "Please choose a date" : null}><input type="date" className="pm-input" value={p.event_date || ""} onChange={set("event_date")} data-testid="voice-date-input" /></Field>
        <Field label={`Time ${p.event_time ? `(${p.time_zone || "your time zone"})` : "(optional)"}`}><input type="time" className="pm-input" value={p.event_time || ""} onChange={(e) => setP({ ...p, event_time: e.target.value, date_kind: e.target.value ? "instant" : "all_day" })} data-testid="voice-time-input" /></Field>
        <Field label="Repeats"><select className="pm-input" value={p.recurrence} onChange={set("recurrence")} data-testid="voice-recurrence-select">
          {Object.entries(RECURRENCE_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></Field>
        <div className="self-end pb-2">{d !== null && <StatusPill urgency={d < 0 ? "overdue" : d <= 30 ? "due_soon" : "upcoming"} testid="voice-countdown">{countdownLabel(d)} · {fmtDate(p.event_date)}</StatusPill>}</div>
      </div>
      <div>
        <p className="pm-label">Remind me</p>
        <div className="flex flex-wrap gap-2">{[...new Set([...PRESET_OFFSETS, ...p.alert_offsets])].sort((a, b) => b - a).map((o) => (
          <button key={o} type="button" className="pm-tab" data-active={p.alert_offsets.includes(o)} onClick={() => toggle(o)} data-testid={`voice-offset-${o}`}>{o === 0 ? "On the day" : `${o}d before`}</button>
        ))}</div>
      </div>
    </div>
  );
}

export function VoiceDialog({ open, onClose }) {
  const nav = useNavigate();
  const { meta } = useMeta();
  const [text, setText] = useState("");
  const [typing, setTyping] = useState(!SR);
  const [busy, setBusy] = useState(false);
  const [p, setP] = useState(null);
  const parse = async (t) => {
    setBusy(true);
    try { const { data } = await api.post("/voice/parse", { text: t }); setP(data); setText(t); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const sp = useSpeech(parse);
  useEffect(() => { if (open) { setP(null); setText(""); setTyping(!SR); } }, [open]);
  useEffect(() => { if (sp.error) setTyping(true); }, [sp.error]);

  const save = async () => {
    if (!p.title.trim() || !p.event_date) { toast.error("A title and date are needed"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/reminders", {
        title: p.title.trim(), category_id: p.category_id, subcategory_id: p.subcategory_id, event_date: p.event_date,
        date_kind: p.event_time ? "instant" : "all_day", event_time: p.event_time || null, time_zone: p.event_time ? p.time_zone || null : null,
        recurrence: p.recurrence, alert_offsets: p.alert_offsets, notes: p.notes || "", tags: ["voice"],
      });
      toast.success("Reminder pinned by voice");
      onClose();
      nav(`/reminders/${data.id}`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto" data-testid="voice-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-[var(--pm-magenta)]" /> Voice Reminder</DialogTitle>
          <DialogDescription>{p ? "Check the details, edit anything, then confirm." : "Tap the microphone and speak naturally."}</DialogDescription>
        </DialogHeader>
        {!p ? (
          <div className="flex flex-col items-center text-center">
            {!typing && (
              <>
                <button onClick={sp.listening ? sp.stop : sp.start} disabled={busy}
                  className={`relative w-24 h-24 rounded-full grid place-items-center text-white my-4 transition-transform hover:scale-105 ${sp.listening ? "animate-pulse" : ""}`}
                  style={{ background: "var(--pm-gradient)", boxShadow: "0 18px 40px -12px rgba(195,0,119,.6)" }} aria-label={sp.listening ? "Stop listening" : "Start listening"} data-testid="voice-mic-btn">
                  {busy ? <Loader2 className="w-9 h-9 animate-spin" /> : sp.listening ? <MicOff className="w-9 h-9" /> : <Mic className="w-9 h-9" />}
                  {sp.listening && <span className="absolute inset-0 rounded-full border-4 border-[#EC1785]/40 animate-ping" />}
                </button>
                <p className="font-semibold" data-testid="voice-status">{busy ? "Understanding…" : sp.listening ? "Listening… tap to stop" : "Tap to speak"}</p>
                <p className="text-sm text-[var(--pm-muted)] mt-2 min-h-[1.5rem]" data-testid="voice-interim">{sp.interim}</p>
              </>
            )}
            {sp.error && <p className="text-sm text-[#8E2433] bg-[#FDECEE] rounded-xl p-3 w-full text-left" role="alert" data-testid="voice-error">{ERRORS[sp.error]}</p>}
            {typing ? (
              <form className="w-full text-left mt-3" onSubmit={(e) => { e.preventDefault(); if (text.trim().length >= 3) parse(text.trim()); }}>
                <Field label="Type your reminder" htmlFor="v-text"><textarea id="v-text" className="pm-input" rows={3} value={text} onChange={(e) => setText(e.target.value)} placeholder={EXAMPLE} data-testid="voice-text-input" /></Field>
                <div className="flex flex-wrap gap-2 mt-3">
                  <button className="pm-btn pm-btn-primary" disabled={busy || text.trim().length < 3} data-testid="voice-text-submit">{busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Understanding…</> : <><Sparkles className="w-4 h-4" /> Understand</>}</button>
                  {SR && sp.error !== "denied" && <button type="button" className="pm-btn pm-btn-outline" onClick={() => { setTyping(false); sp.start(); }} data-testid="voice-switch-mic"><Mic className="w-4 h-4" /> Use microphone</button>}
                </div>
              </form>
            ) : (
              <button className="text-sm font-semibold text-[var(--pm-primary)] mt-4 flex items-center gap-1.5" onClick={() => { sp.stop(); setTyping(true); }} data-testid="voice-switch-text"><Keyboard className="w-4 h-4" /> Type instead</button>
            )}
            <p className="text-xs text-[var(--pm-muted)] mt-5">Try: “{EXAMPLE}”</p>
          </div>
        ) : (
          <>
            <Preview p={p} setP={setP} meta={meta} onReparse={parse} busy={busy} />
            <div className="flex flex-wrap justify-between gap-2 mt-2 pt-4 border-t border-[var(--pm-border)]">
              <button className="pm-btn pm-btn-outline" onClick={() => { setP(null); setText(""); }} data-testid="voice-retry-btn"><RotateCcw className="w-4 h-4" /> Start over</button>
              <button className="pm-btn pm-btn-primary" onClick={save} disabled={busy || !p.event_date || !p.title.trim()} data-testid="voice-save-btn"><Check className="w-4 h-4" /> {busy ? "Saving…" : "Confirm & Save"}</button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function VoiceButton({ variant = "button", testid = "voice-open-btn" }) {
  const [open, setOpen] = useState(false);
  const btn = variant === "icon" ? (
    <button onClick={() => setOpen(true)} className="w-10 h-10 rounded-full grid place-items-center text-white" style={{ background: "var(--pm-gradient)" }} aria-label="Voice reminder" data-testid={testid}><Mic className="w-5 h-5" /></button>
  ) : variant === "card" ? (
    <button onClick={() => setOpen(true)} className="pm-card p-7 text-left hover:-translate-y-1 hover:border-[#CFC8EA] transition-transform" data-testid={testid}>
      <span className="w-12 h-12 rounded-2xl grid place-items-center text-white" style={{ background: "var(--pm-gradient)" }}><Mic className="w-5 h-5" /></span>
      <h3 className="font-bold text-lg mt-5">Voice Reminder</h3>
      <p className="text-sm text-[var(--pm-muted)] mt-1">Just say it — AI fills in the title, date, time, repeat and alerts.</p>
    </button>
  ) : (
    <button onClick={() => setOpen(true)} className="pm-btn pm-btn-soft" data-testid={testid}><Mic className="w-4 h-4" /> Voice</button>
  );
  return <>{btn}<VoiceDialog open={open} onClose={() => setOpen(false)} /></>;
}
